
import React, { useState, useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  FaTimes, FaQrcode, FaCopy, FaDownload, FaLock,
  FaUnlock, FaLink, FaExternalLinkAlt,
  FaInfoCircle,  FaCheck, FaExpand
} from 'react-icons/fa';
import { evalApi, buildEvalLink } from '../hooks/useEvalApi';

const Qrcodepanel = ({ formation, onClose, onToggleQR, isDark, c }) => {
  const [qrActif,  setQrActif]  = useState(formation?.qr_actif ?? false);
  const [copied,   setCopied]   = useState(false);
  const [toggling, setToggling] = useState(false);
  const [toggErr,  setToggErr]  = useState('');
  const qrRef = useRef(null);

  const link = buildEvalLink(formation?.token);

  const handleCopy = useCallback(async () => {
    try { await navigator.clipboard.writeText(link); } catch { /* noop */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [link]);

  // ── Téléchargement SVG ──────────────────────────────────────────────────
  const handleDownloadSVG = useCallback(() => {
    if (!qrRef.current) return;
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const blob    = new Blob([svgData], { type: 'image/svg+xml' });
    const url     = URL.createObjectURL(blob);
    const a       = document.createElement('a');
    a.href        = url;
    a.download    = `QR_${formation?.id}_${(formation?.titre ?? '').slice(0, 20).replace(/\s+/g, '_')}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  }, [formation]);

  // ── Téléchargement PNG ──────────────────────────────────────────────────
  const handleDownloadPNG = useCallback(() => {
    if (!qrRef.current) return;
    const svg     = qrRef.current.querySelector('svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const img     = new Image();
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url     = URL.createObjectURL(svgBlob);
    img.onload    = () => {
      const canvas       = document.createElement('canvas');
      canvas.width       = 400;
      canvas.height      = 400;
      const ctx          = canvas.getContext('2d');
      ctx.fillStyle      = '#ffffff';
      ctx.fillRect(0, 0, 400, 400);
      ctx.drawImage(img, 0, 0, 400, 400);
      URL.revokeObjectURL(url);
      const pngUrl = canvas.toDataURL('image/png');
      const a      = document.createElement('a');
      a.href       = pngUrl;
      a.download   = `QR_${formation?.id}.png`;
      a.click();
    };
    img.src = url;
  }, [formation]);

  // ── Toggle QR — branché sur l'API ───────────────────────────────────────
  const handleToggle = useCallback(async () => {
    setToggling(true);
    setToggErr('');
    try {
      const res  = await evalApi.toggleQR(formation.id);
      const next = res.data.qr_actif;
      setQrActif(next);
      onToggleQR?.(next);
    } catch (err) {
      setToggErr(err.response?.data?.message || 'Erreur lors de la mise à jour.');
    } finally {
      setToggling(false);
    }
  }, [formation, onToggleQR]);

  // ── Projeter QR en plein écran — VERSION CORRIGÉE ───────────────────────
  // On génère le SVG en inline string plutôt que d'essayer de le sérialiser depuis le DOM
  const handleProjectFullscreen = useCallback(() => {
    // Générer le SVG du QR manuellement via la lib qrcode
    // On utilise l'élément DOM existant comme source
    const svgEl = qrRef.current?.querySelector('svg');
    let svgString = '';
    if (svgEl) {
      svgString = new XMLSerializer().serializeToString(svgEl);
      // Forcer width/height à 100% pour l'affichage plein écran
      svgString = svgString.replace(/width="[^"]*"/, 'width="320"').replace(/height="[^"]*"/, 'height="320"');
    }

    const svgDataUri = svgString
      ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`
      : '';

    const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>QR Code — Évaluation</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body {
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: #ffffff;
      font-family: 'Segoe UI', sans-serif;
      gap: 28px;
      padding: 40px 20px;
    }
    .logo-bar {
      position: fixed; top: 24px; left: 50%; transform: translateX(-50%);
      background: #1a2535; color: #fff;
      padding: 8px 22px; border-radius: 30px;
      font-size: 14px; font-weight: 700; letter-spacing: 0.5px;
    }
    .logo-bar span { color: #fd7e14; }
    .titre {
      font-size: clamp(16px, 2.5vw, 22px);
      font-weight: 800;
      color: #198754;
      text-align: center;
      max-width: 600px;
      line-height: 1.4;
    }
    .meta {
      font-size: 14px;
      color: #718096;
      text-align: center;
    }
    .qr-wrap {
      padding: 28px;
      border-radius: 24px;
      box-shadow: 0 12px 48px rgba(0,0,0,0.12);
      border: 2px solid #e2e8f0;
      background: #fff;
    }
    .qr-wrap img {
      display: block;
      width: 320px;
      height: 320px;
    }
    .instruction {
      font-size: 18px;
      font-weight: 700;
      color: #fd7e14;
      text-align: center;
    }
    .url-pill {
      font-family: monospace;
      font-size: 13px;
      color: #adb5bd;
      background: #f8fafc;
      padding: 8px 18px;
      border-radius: 20px;
      border: 1px solid #e2e8f0;
      word-break: break-all;
      text-align: center;
      max-width: 500px;
    }
  </style>
</head>
<body>
  <div class="logo-bar">CINEF <span>Formation</span></div>
  <div class="titre">${(formation?.titre ?? '').replace(/</g, '&lt;')}</div>
  <div class="meta">${(formation?.dates ?? '').replace(/</g, '&lt;')} · ${(formation?.lieu ?? '').replace(/</g, '&lt;')}</div>
  <div class="qr-wrap">
    ${svgString
      ? `<img src="${svgDataUri}" alt="QR Code" />`
      : `<div style="width:320px;height:320px;display:flex;align-items:center;justify-content:center;color:#adb5bd;font-size:14px;">QR indisponible</div>`
    }
  </div>
  <div class="instruction">📱 Scannez ce QR code pour évaluer la formation</div>
  <div class="url-pill">${link}</div>
</body>
</html>`;

    const blob   = new Blob([html], { type: 'text/html' });
    const url    = URL.createObjectURL(blob);
    const win    = window.open(url, '_blank', 'width=900,height=700');
    // Libérer l'URL objet après ouverture
    setTimeout(() => URL.revokeObjectURL(url), 5000);

    // Si popup bloquée, fallback : ouvrir dans le même onglet
    if (!win) {
      const a      = document.createElement('a');
      a.href       = url;
      a.target     = '_blank';
      a.click();
    }
  }, [formation, link]);

  const avisCount     = formation?.reponses_count ?? formation?.avis?.length ?? 0;
  const nbAttendu     = formation?.participants_attendus ?? 0;
  const participation = nbAttendu ? Math.round((avisCount / nbAttendu) * 100) : 0;

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: isDark ? 'rgba(9,12,22,0.82)' : 'rgba(15,23,42,0.55)',
        backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 3000, padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: c.modalBg, border: `1px solid ${c.cardB}`,
          borderRadius: 22, width: '100%', maxWidth: 520,
          maxHeight: '92vh', overflowY: 'auto',
          boxShadow: '0 28px 72px rgba(0,0,0,0.24)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 22px', borderBottom: `1px solid ${c.divider}`,
          position: 'sticky', top: 0, background: c.modalBg,
          borderRadius: '22px 22px 0 0', zIndex: 1,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 38, height: 38, borderRadius: 11, background: 'rgba(25,135,84,0.12)', color: '#198754', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
              <FaQrcode />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: c.title }}>Gestion du QR Code</div>
              <div style={{ fontSize: 12, color: c.textDim }}>Formulaire d'évaluation participant</div>
            </div>
          </div>
          <button onClick={onClose} style={{ width: 34, height: 34, borderRadius: 9, border: `1.5px solid ${c.cardB}`, background: 'transparent', color: c.textDim, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
            <FaTimes />
          </button>
        </div>

        <div style={{ padding: '22px 24px' }}>

          {/* Badge formation */}
          <div style={{ background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc', border: `1px solid ${c.cardB}`, borderRadius: 12, padding: '13px 16px', marginBottom: 20 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: c.title, marginBottom: 3 }}>{formation?.titre}</div>
            <div style={{ fontSize: 12, color: c.textDim }}>{formation?.dates} · {formation?.lieu}</div>
          </div>

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 20 }}>
            {[
              { val: avisCount,     lbl: 'Avis reçus' },
              { val: nbAttendu,     lbl: 'Attendus'   },
              { val: `${participation}%`, lbl: 'Participation', color: '#198754' },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: 'center', padding: '14px 10px', background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc', border: `1px solid ${c.cardB}`, borderRadius: 12 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: s.color || c.title }}>{s.val}</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: c.textDim, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 2 }}>{s.lbl}</div>
              </div>
            ))}
          </div>

          {/* Erreur toggle */}
          {toggErr && (
            <div style={{ padding: '10px 14px', background: 'rgba(220,53,69,0.08)', border: '1px solid rgba(220,53,69,0.25)', borderRadius: 10, fontSize: 13, color: '#dc3545', fontWeight: 600, marginBottom: 14 }}>
              {toggErr}
            </div>
          )}

          {/* Statut & toggle */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '14px 18px', borderRadius: 13,
            border: `1.5px solid ${qrActif ? 'rgba(25,135,84,0.3)' : c.cardB}`,
            background: qrActif ? 'rgba(25,135,84,0.06)' : isDark ? 'rgba(255,255,255,0.03)' : '#fafbfc',
            marginBottom: 22, transition: 'all 0.3s',
          }}>
            {qrActif
              ? <FaUnlock style={{ color: '#198754', fontSize: 16, flexShrink: 0 }} />
              : <FaLock   style={{ color: c.textDim, fontSize: 15, flexShrink: 0 }} />
            }
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: qrActif ? '#198754' : c.title }}>
                {qrActif ? 'QR Code actif — formulaire ouvert' : 'QR Code inactif — formulaire fermé'}
              </div>
              <div style={{ fontSize: 12, color: c.textDim, marginTop: 1 }}>
                {qrActif
                  ? 'Les participants peuvent scanner et remplir le formulaire.'
                  : 'Activez le QR code pour ouvrir le formulaire aux participants.'}
              </div>
            </div>
            <button
              onClick={handleToggle}
              disabled={toggling}
              style={{
                padding: '9px 16px', borderRadius: 9,
                border: qrActif ? '1.5px solid rgba(220,53,69,0.25)' : 'none',
                background: qrActif ? 'rgba(220,53,69,0.08)' : '#198754',
                color: qrActif ? '#dc3545' : '#fff',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                cursor: toggling ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', gap: 7,
                opacity: toggling ? 0.65 : 1, flexShrink: 0,
              }}
            >
              {toggling ? '…'
                : qrActif
                  ? <><FaLock   style={{ fontSize: 11 }} /> Désactiver</>
                  : <><FaUnlock style={{ fontSize: 11 }} /> Activer</>
              }
            </button>
          </div>

          {/* QR Code visuel */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}>
            <div
              ref={qrRef}
              style={{
                padding: 20, borderRadius: 18, background: '#ffffff',
                border: `1.5px solid ${c.cardB}`,
                boxShadow: '0 8px 28px rgba(0,0,0,0.08)',
                position: 'relative', display: 'inline-block',
              }}
            >
              <QRCodeSVG value={link} size={200} level="H" />
              {!qrActif && (
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'rgba(255,255,255,0.88)',
                  backdropFilter: 'blur(4px)', borderRadius: 17,
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 8,
                }}>
                  <FaLock style={{ fontSize: 28, color: '#adb5bd' }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#adb5bd' }}>QR inactif</span>
                </div>
              )}
            </div>
          </div>

          {/* Bouton Projeter — VERSION CORRIGÉE */}
          <button
            onClick={handleProjectFullscreen}
            style={{
              width: '100%', padding: '11px', borderRadius: 10,
              border: `1.5px solid ${c.cardB}`, background: 'transparent',
              color: c.textDim, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 7, fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              marginBottom: 14, transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#198754'; e.currentTarget.style.color = '#198754'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = c.cardB;   e.currentTarget.style.color = c.textDim; }}
          >
            <FaExpand /> Projeter le QR code (plein écran)
          </button>

          {/* Lien */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${c.inputB}`, background: c.input }}>
              <FaLink style={{ color: c.textDim, fontSize: 12, flexShrink: 0 }} />
              <span style={{ fontFamily: "'Courier New', monospace", fontSize: 12, color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{link}</span>
            </div>
            <button
              onClick={handleCopy}
              style={{
                padding: '11px 14px', borderRadius: 10,
                border: `1.5px solid ${copied ? '#198754' : c.cardB}`,
                background: copied ? 'rgba(25,135,84,0.08)' : 'transparent',
                color: copied ? '#198754' : c.textDim,
                cursor: 'pointer', display: 'flex', alignItems: 'center',
                gap: 6, fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                transition: 'all 0.2s', whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              {copied ? <><FaCheck style={{ fontSize: 11 }} /> Copié !</> : <><FaCopy /> Copier</>}
            </button>
            <a
              href={link} target="_blank" rel="noopener noreferrer"
              style={{ padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${c.cardB}`, background: 'transparent', color: c.textDim, display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', transition: 'all 0.2s', flexShrink: 0 }}
            >
              <FaExternalLinkAlt style={{ fontSize: 13 }} />
            </a>
          </div>

          {/* Téléchargements */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
            {[
              { label: 'Télécharger SVG', fn: handleDownloadSVG },
              { label: 'Télécharger PNG', fn: handleDownloadPNG },
            ].map(btn => (
              <button key={btn.label} onClick={btn.fn} disabled={!qrActif}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 8, padding: 12, borderRadius: 11,
                  border: `1.5px solid ${c.cardB}`, background: 'transparent',
                  color: c.textDim, fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                  cursor: qrActif ? 'pointer' : 'not-allowed', opacity: qrActif ? 1 : 0.4, transition: 'all 0.2s',
                }}
              >
                <FaDownload style={{ fontSize: 13 }} /> {btn.label}
              </button>
            ))}
          </div>

          {/* Info box */}
          <div style={{ display: 'flex', gap: 10, padding: '13px 16px', borderRadius: 11, background: isDark ? 'rgba(255,193,7,0.06)' : 'rgba(255,193,7,0.08)', border: '1px solid rgba(255,193,7,0.25)', fontSize: 13, color: c.text, lineHeight: 1.6 }}>
            <FaInfoCircle style={{ color: '#ffc107', flexShrink: 0, marginTop: 2, fontSize: 15 }} />
            <div>Désactivez le QR après la clôture de la formation pour empêcher de nouvelles soumissions. Le lien est unique par session.</div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Qrcodepanel;